import '@/styles/themes.css';
import builderBanner from '@/assets/themes/build-custom-theme.jpeg';
import passBanner from '@/assets/themes/all-access-pass.jpeg';
import {Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@tryghost/shade';
import {Link} from 'react-router-dom';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {Select} from '@/components/ui/Select';
import {getSSOLink} from '@/api/sites';
import {useMarketplaceCheckout} from '@/hooks/useMarketplaceCheckout';
import {useMarketplaceThemes} from '@/hooks/useMarketplaceThemes';
import {useMemo, useState} from 'react';
import {useSites} from '@/hooks/useSites';
import type {BundleProduct, Theme} from '@/api/marketplace';

function formatPrice(cents: number | null): string {
    if (cents === null) {
        return 'Free';
    }
    return `$${formatNumber(Math.floor(cents / 100))}.${String(cents % 100).padStart(2, '0')}`;
}

function AllAccessBundleHero({isActive, products, pending, error, onCheckout}: {isActive: boolean; products: BundleProduct[]; pending: boolean; error: string | null; onCheckout: (productId: string) => void}) {
    const [open, setOpen] = useState(false);
    return <>
        <section aria-labelledby="pass-heading" className="themes-pass-banner">
            <img alt="" aria-hidden="true" className="themes-pass-background" src={passBanner} />
            <h2 id="pass-heading">All Access <br />Pass</h2>
            <div className="themes-pass-body"><p>Every premium theme,<br />for every newsletter.</p><span>Choose a monthly, annual, or lifetime pass.</span></div>
            {isActive ? <span className="themes-pass-status">Your pass is active <LucideIcon.Check size={16} /></span> : <Button className="theme-button themes-pass-action" type="button" variant="outline" onClick={() => setOpen(true)}>View pass options <LucideIcon.ArrowRight size={15} /></Button>}
        </section>
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="theme-workspace-popover themes-pass-dialog">
                <DialogClose asChild><Button aria-label="Close pass options" className="themes-dialog-close" type="button" variant="ghost"><LucideIcon.X size={18} /></Button></DialogClose>
                <DialogHeader><DialogTitle>All Access Pass</DialogTitle><DialogDescription>Every premium theme, for every newsletter. Choose your pass to continue to checkout.</DialogDescription></DialogHeader>
                <div className="themes-pass-options">{products.map(product => <Button key={product.id} className="theme-button theme-button-secondary" disabled={pending} type="button" variant="outline" onClick={() => onCheckout(product.id)}>{product.term}<LucideIcon.ArrowRight size={16} /></Button>)}</div>
                {!products.length && <p className="theme-muted">Pass options are currently unavailable. Please try again later.</p>}
                {pending && <p role="status">Opening checkout…</p>}
                {error && <p className="theme-error" role="alert">{error}</p>}
            </DialogContent>
        </Dialog>
    </>;
}

function ThemeCard({theme, onBuy, onInstall, onPreview, pending, billingPortalUrl}: {theme: Theme; onBuy: (item: Theme) => void; onInstall: (item: Theme) => void; onPreview: (item: Theme) => void; pending: boolean; billingPortalUrl: string | null}) {
    const [failedThumbnail, setFailedThumbnail] = useState<string | null>(null);
    const isOwned = theme.state === 'owned';
    const isFree = theme.state === 'free';
    const isAllAccess = ['bundle_active', 'bundle_grace'].includes(theme.entitlement_reason);
    const thumbnail = theme.thumbnail_url && failedThumbnail !== theme.thumbnail_url ? <img alt={`${theme.name} theme screenshot`} loading="lazy" src={theme.thumbnail_url} onError={() => setFailedThumbnail(theme.thumbnail_url)} /> : <div aria-label={`${theme.name} theme preview unavailable`} className="themes-image-placeholder"><LucideIcon.Image size={23} strokeWidth={1.3} /><span>Preview unavailable</span></div>;
    return <article className="themes-card" data-testid="theme-card">
        <div className="themes-card-image">
            {theme.preview_url ? <button aria-label={`Open interactive preview for ${theme.name}`} className="themes-card-preview-trigger" type="button" onClick={() => onPreview(theme)}>{thumbnail}<span className="themes-card-preview-hint"><LucideIcon.Expand size={15} />Interactive preview</span></button> : thumbnail}
            {theme.installed_version && <span className="themes-installed"><LucideIcon.Check size={12} />Installed</span>}
        </div>
        <div className="themes-card-content">
            <div className="themes-card-title"><h3>{theme.name}</h3><span className="themes-badge">{theme.visibility === 'private' ? 'Private' : isAllAccess ? 'All Access' : isOwned ? 'Owned' : isFree ? 'Free' : 'Premium'}</span></div>
            <p>{theme.tagline || theme.description || 'No description provided.'}</p>
            <div className="themes-card-footer">
                {theme.included_with_all_access && <span className="themes-inclusion">{isAllAccess ? 'Included with your All Access Pass' : 'Included with All Access Pass'}</span>}
                <div className="themes-card-actions">
                    {isOwned || isFree ? <Button className="theme-button theme-button-secondary" disabled={pending} type="button" variant="outline" onClick={() => onInstall(theme)}>{theme.visibility === 'private' ? 'Use in newsletter' : theme.update_available ? 'Update in Admin' : 'Install in Newsletter'}</Button> : <Button className="theme-button theme-button-secondary" disabled={pending} type="button" variant="outline" onClick={() => onBuy(theme)}>Buy {formatPrice(theme.price_cents)}</Button>}
                    {theme.preview_url && <Button className="theme-button theme-button-secondary" type="button" variant="outline" onClick={() => onPreview(theme)}>Preview <LucideIcon.Expand size={14} /></Button>}
                    {isOwned && !isFree && theme.visibility !== 'private' && billingPortalUrl && <a className="themes-billing-link" href={billingPortalUrl} rel="noopener noreferrer" target="_blank">Manage billing</a>}
                </div>
            </div>
        </div>
    </article>;
}

function ThemePreviewDialog({theme, onClose}: {theme: Theme | null; onClose: () => void}) {
    const officialGhostDemo = (() => {
        try {
            const url = new URL(theme?.preview_url || '');
            return url.protocol === 'https:' && /^[a-z0-9-]+\.ghost\.io$/.test(url.hostname);
        } catch {
            return false;
        }
    })();

    return <Dialog open={Boolean(theme?.preview_url)} onOpenChange={open => !open && onClose()}>
        <DialogContent className="theme-workspace-popover themes-preview-dialog">
            <div className="themes-preview-dialog-header">
                <DialogHeader><DialogTitle>{theme?.name} preview</DialogTitle><DialogDescription>Explore the theme as visitors will see it.</DialogDescription></DialogHeader>
                <div className="themes-preview-dialog-actions">
                    {theme?.preview_url && <a className="theme-button theme-button-secondary" href={theme.preview_url} rel="noopener noreferrer" target="_blank">Open in new tab <LucideIcon.ArrowUpRight size={14} /></a>}
                    <DialogClose asChild><Button aria-label="Close theme preview" className="themes-dialog-close" type="button" variant="ghost"><LucideIcon.X size={18} /></Button></DialogClose>
                </div>
            </div>
            {theme?.preview_url && <iframe className="themes-preview-frame" referrerPolicy="no-referrer" sandbox={officialGhostDemo ? 'allow-scripts allow-same-origin' : 'allow-same-origin'} src={theme.preview_url} title={`${theme.name} interactive preview`} />}
        </DialogContent>
    </Dialog>;
}

type FilterType = 'All themes' | 'Free' | 'Premium' | 'Owned' | 'Installed';

export function ThemeStorePage() {
    const {data, isLoading, isError, refetch} = useMarketplaceThemes();
    const {data: sites} = useSites();
    const checkout = useMarketplaceCheckout();
    const [filter, setFilter] = useState<FilterType>('All themes');
    const [search, setSearch] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);
    const [installing, setInstalling] = useState(false);
    const [installTheme, setInstallTheme] = useState<Theme | null>(null);
    const [installSiteId, setInstallSiteId] = useState('');
    const [installError, setInstallError] = useState<string | null>(null);
    const [previewTheme, setPreviewTheme] = useState<Theme | null>(null);
    const ownedSites = sites?.filter(site => site.status === 'active' && site.access_type === 'owner') || [];
    const pending = checkout.isLoading || installing;
    const filteredThemes = useMemo(() => (data?.themes || []).filter(theme => theme.visibility !== 'private').filter((theme) => {
        const matchesFilter = filter === 'Free' ? theme.is_free : filter === 'Premium' ? !theme.is_free : filter === 'Owned' ? theme.state === 'owned' : filter === 'Installed' ? Boolean(theme.installed_version) : true;
        const query = search.trim().toLowerCase();
        return matchesFilter && (!query || [theme.name, theme.description, theme.tagline].some(value => value?.toLowerCase().includes(query)));
    }), [data?.themes, filter, search]);

    const handleCheckout = async (productId: string | null) => {
        if (!productId) {
            setActionError('This theme is not currently available for checkout.');
            return;
        }
        setActionError(null);
        try {
            const result = await checkout.mutateAsync({productId});
            window.location.href = result.checkoutUrl;
        } catch {
            setActionError('Checkout could not be started. Please retry.');
        }
    };
    const handleOpenAdmin = async () => {
        const selectedSite = ownedSites.find(site => site.id === installSiteId);
        if (!selectedSite) {
            setInstallError('Choose a newsletter to continue.');
            return;
        }
        setInstallError(null);
        setInstalling(true);
        const adminTab = window.open('', '_blank');
        if (adminTab) {
            adminTab.opener = null;
        }
        try {
            const destination = await getSSOLink(selectedSite.id, 'settings/design/change-theme');
            if (adminTab) {
                adminTab.location.href = destination;
            } else {
                window.location.href = destination;
            }
            setInstallTheme(null);
            setInstallSiteId('');
        } catch {
            adminTab?.close();
            setInstallError('Ghost Admin could not be opened. Please retry.');
        } finally {
            setInstalling(false);
        }
    };
    return <div className="theme-store-page">
        <ThemePreviewDialog theme={previewTheme} onClose={() => setPreviewTheme(null)} />
        <Dialog open={Boolean(installTheme)} onOpenChange={(open) => {
            if (!open && !installing) {
                setInstallTheme(null);
                setInstallSiteId('');
                setInstallError(null);
            }
        }}>
            <DialogContent className="theme-workspace-popover themes-install-dialog">
                <DialogClose asChild><Button aria-label="Close newsletter selector" className="themes-dialog-close" disabled={installing} type="button" variant="ghost"><LucideIcon.X size={18} /></Button></DialogClose>
                <DialogHeader><DialogTitle>Choose a newsletter</DialogTitle><DialogDescription>Select where you want to use {installTheme?.name}.</DialogDescription></DialogHeader>
                {ownedSites.length ? <div className="themes-install-field"><label htmlFor="theme-install-site">Newsletter</label><Select id="theme-install-site" options={ownedSites.map(site => ({value: site.id, label: site.name}))} placeholder="Choose a newsletter" value={installSiteId} width="100%" onChange={(value) => {
                    setInstallSiteId(value);
                    setInstallError(null);
                }} /></div> : <p className="theme-muted">You need an active newsletter you own to continue.</p>}
                {installError && <p className="theme-error" role="alert">{installError}</p>}
                <Button className="theme-button theme-button-primary themes-install-action" disabled={!installSiteId || installing} type="button" onClick={handleOpenAdmin}>{installing ? 'Opening Admin…' : 'Open in Admin'}</Button>
            </DialogContent>
        </Dialog>
        <header className="themes-page-heading"><h1>Themes</h1><p>Find a design that fits your newsletter.</p></header>
        <section aria-labelledby="custom-theme-title" className="themes-builder-banner">
            <div className="themes-builder-copy"><h2 id="custom-theme-title">Build a custom <br />theme.</h2><p>Create a theme from your publication brief, logo, and design references.</p>
                <Link className="theme-button theme-button-primary" to="/themes/builder">Build custom theme <LucideIcon.ArrowRight size={15} /></Link>
            </div>
            <div className="themes-builder-image"><img alt="Editorial, newsletter, and magazine theme layouts with terracotta accents" src={builderBanner} /></div>
        </section>
        {!isLoading && data && <AllAccessBundleHero error={actionError} isActive={Boolean(data.bundle && ['active', 'grace'].includes(data.bundle.status))} pending={Boolean(checkout.isLoading)} products={data.bundleProducts || []} onCheckout={handleCheckout} />}
        <section aria-label="Theme catalog">
            <div className="themes-catalog-toolbar"><div aria-label="Filter themes" className="themes-filters" role="group">{(['All themes', 'Free', 'Premium', 'Owned', 'Installed'] as FilterType[]).map(item => <button key={item} aria-pressed={filter === item} className="themes-filter" type="button" onClick={() => setFilter(item)}>{item}</button>)}</div><label className="themes-search"><LucideIcon.Search aria-hidden="true" size={16} /><input aria-label="Search themes" placeholder="Search themes..." type="search" value={search} onChange={event => setSearch(event.target.value)} /></label></div>
            {actionError && <div className="themes-action-error" role="alert">{actionError}<Button className="theme-button" type="button" variant="ghost" onClick={() => setActionError(null)}>Dismiss</Button></div>}
            {isLoading ? <div aria-label="Loading themes" className="themes-grid" role="status">{['first', 'second', 'third'].map(key => <div key={key} className="themes-card themes-skeleton"><div /><span /><span /></div>)}</div> : isError ? <div className="themes-empty"><h2>Unable to load themes</h2><p>Please try again.</p><Button className="theme-button theme-button-secondary" type="button" variant="outline" onClick={() => refetch()}>Retry</Button></div> : <>
                <p aria-live="polite" className="theme-sr-only">{formatNumber(filteredThemes.length)} themes found</p>
                {filteredThemes.length ? <div className="themes-grid">{filteredThemes.map(theme => <ThemeCard key={theme.id} billingPortalUrl={data?.billing_portal_url || null} pending={Boolean(pending)} theme={theme} onBuy={item => handleCheckout(item.product_id)} onInstall={(item) => {
                    setInstallTheme(item);
                    setInstallSiteId('');
                    setInstallError(null);
                }} onPreview={setPreviewTheme} />)}</div> : <div className="themes-empty"><h2>No themes found</h2><p>Try another search or filter.</p><Button className="theme-button theme-button-secondary" type="button" variant="outline" onClick={() => {
                    setFilter('All themes'); setSearch('');
                }}>Show all themes</Button></div>}
            </>}
        </section>
    </div>;
}
